import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Package, 
  DollarSign, 
  Brain,
  Calendar,
  Download,
  Settings,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { api } from '@/services/api';

// Format utility functions
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-MW', {
    style: 'currency',
    currency: 'MWK',
  }).format(amount);
};

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

interface BusinessMetric {
  id: string;
  name: string;
  value: number;
  unit: string;
  comparison?: {
    change: number;
    changePercent: number;
  };
}

interface Insight {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  recommendations: string[];
  createdAt: string;
}

const BusinessIntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'analytics' | 'insights' | 'reports'>('dashboard');

  // Fetch dashboard data
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['bi-dashboard'],
    queryFn: async () => {
      const res = await api.get('/api/business-intelligence/dashboard');
      return res.data;
    }
  });

  // Fetch insights
  const { data: insightsData } = useQuery({
    queryKey: ['bi-insights'],
    queryFn: async () => {
      const res = await api.get('/api/business-intelligence/insights');
      return res.data;
    }
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const renderMetricCard = (metric: BusinessMetric, icon: React.ReactNode) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="p-2 bg-blue-50 rounded-lg">
          {icon}
        </div>
        {metric.comparison && (
          <div className={`flex items-center text-sm ${getChangeColor(metric.comparison.change)}`}>
            {metric.comparison.change > 0 ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-1 rotate-180" />
            )}
            {Math.abs(metric.comparison.changePercent).toFixed(1)}%
          </div>
        )}
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">{metric.name}</p>
        <p className="text-2xl font-bold text-gray-900">
          {metric.unit === 'currency' ? formatCurrency(metric.value) : formatNumber(metric.value)}
        </p>
      </div>
    </motion.div>
  );

  const renderInsightCard = (insight: Insight) => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <Brain className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="font-semibold text-gray-900">{insight.title}</h3>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(insight.severity)}`}>
          {insight.severity}
        </span>
      </div>
      <p className="text-gray-600 mb-4">{insight.description}</p>
      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
        <span>Confidence: {Math.round(insight.confidence * 100)}%</span>
        <span>{new Date(insight.createdAt).toLocaleDateString()}</span>
      </div>
      {insight.recommendations.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Recommendations:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            {insight.recommendations.slice(0, 2).map((rec, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );

  if (isLoadingDashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Business Intelligence</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button variant="ghost" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
              { id: 'insights', label: 'Insights', icon: Brain },
              { id: 'reports', label: 'Reports', icon: Calendar }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center px-1 py-4 border-b-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Key Metrics */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Key Metrics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardData?.data?.metrics?.slice(0, 4).map((metric: BusinessMetric, index: number) => (
                  <motion.div
                    key={metric.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {renderMetricCard(
                      metric,
                      metric.name.toLowerCase().includes('revenue') ? (
                        <DollarSign className="w-6 h-6 text-green-600" />
                      ) : metric.name.toLowerCase().includes('sales') ? (
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      ) : metric.name.toLowerCase().includes('customer') ? (
                        <Users className="w-6 h-6 text-purple-600" />
                      ) : (
                        <Package className="w-6 h-6 text-orange-600" />
                      )
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Recent Insights */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Recent Insights</h2>
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {insightsData?.data?.slice(0, 4).map((insight: Insight, index: number) => (
                  <motion.div
                    key={insight.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {renderInsightCard(insight)}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Sales Analytics */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Performance</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Revenue</span>
                    <span className="font-semibold">{formatCurrency(0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Orders</span>
                    <span className="font-semibold">{formatNumber(0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Average Order Value</span>
                    <span className="font-semibold">{formatCurrency(0)}</span>
                  </div>
                </div>
              </div>

              {/* Customer Analytics */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Analytics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Customers</span>
                    <span className="font-semibold">{formatNumber(0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">New Customers</span>
                    <span className="font-semibold">{formatNumber(0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Retention Rate</span>
                    <span className="font-semibold">0%</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'insights' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Business Insights</h2>
              <div className="flex items-center space-x-4">
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option>All Severities</option>
                  <option>Critical</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option>All Types</option>
                  <option>Sales</option>
                  <option>Inventory</option>
                  <option>Customer</option>
                  <option>Financial</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {insightsData?.data?.map((insight: Insight, index: number) => (
                <motion.div
                  key={insight.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {renderInsightCard(insight)}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'reports' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
              <Button>
                <Calendar className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Yet</h3>
              <p className="text-gray-600 mb-6">Create your first business intelligence report to get started.</p>
              <Button>
                <Calendar className="w-4 h-4 mr-2" />
                Create Report
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BusinessIntelligencePage;
